import type { TCountry } from '@shared/types/country';
import type { TMember } from '@shared/types/member';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { object, string } from 'yup';

export interface IPersonalInfo {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  whatsapp_number?: string;
  avatar?: string;
  id?: number;
}
export interface IAdditionalInfo {
  date_of_birth: string;
  gender?: string;
  nationality: string;
  marital_status?: string;
}

export interface TContactInfoValues {
  personal_email: string;
  personal_phone_number: string;
}

export const generatePersonalInfoInitialValue = (
  member: TMember
): IPersonalInfo => ({
  first_name: member.user.first_name || '',
  last_name: member.user.last_name || '',
  email: member.user.email || '',
  phone_number: member.user.phone_number || '',
  whatsapp_number: member.user.whatsapp_number || '',
  avatar: member.user.avatar || '',
  id: member.user.id || 0,
});

export const personalInfoSchema = object({
  first_name: string().required('First Name is required'),
  last_name: string().required('Last Name is required'),
  email: string().email().required('Email is required'),
  phone_number: string()
    .required('Phone is required')
    .test(
      'primary_phone_number',
      'Please enter a valid phone number',
      (value) => {
        return isValidPhoneNumber(value);
      }
    ),
  whatsapp_number: string().test(
    'whatsapp_number',
    'Please enter a valid phone number',
    (value) => {
      return value ? isValidPhoneNumber(value) : true;
    }
  ),
  date_of_birth: string().required('Date of birth is required'),
  gender: string(),
  marital_status: string(),
  nationality: string().required('Nationality is required'),
  avatar: string().optional(),
});

export const generateAddressFormInitialValue = (member: TMember) => ({
  id: member.address?.id || 0,
  address_line_1: member.address?.address_line_1 || '',
  address_line_2: member.address?.address_line_2 || '',
  city: member.address?.city || '',
  state: member.address?.state || '',
  country: (member.address?.country as TCountry)?.code || '',
  postal_code: member.address?.postal_code || '',
});

export const addressSchema = object({
  address_line_1: string().required('Address Line 1 is required'),
  address_line_2: string().optional(),
  city: string().required('City is required'),
  state: string().optional(),
  country: string().required('Country is required'),
  postal_code: string().optional(),
});

export const generateEmergencyContactInfoInitialValue = (member: TMember) => ({
  id: member.emergency_contact_info?.id || 0,
  name: member.emergency_contact_info?.name || '',
  phone_number: member.emergency_contact_info?.phone_number || '',
  email: member.emergency_contact_info?.email || '',
  relationship: member.emergency_contact_info?.relationship || '',
});

export const emergencyContactSchema = object({
  name: string().required('Name is required'),
  phone_number: string()
    .required('Phone is required')
    .test('phone_number', 'Please enter a valid phone number', (value) => {
      return isValidPhoneNumber(value);
    }),
  email: string().email().optional(),
  relationship: string().required('Relationship is required'),
});

export const contactInfoSchema = object({
  personal_email: string().email().required('Email is required'),
  personal_phone_number: string()
    .required('Phone is required')
    .test(
      'personal_phone_number',
      'Please enter a valid phone number',
      (value) => {
        return isValidPhoneNumber(value);
      }
    ),
});
export const generateContactInfo = (
  member: TContactInfoValues
): TContactInfoValues => ({
  personal_email: member?.personal_email || '',
  personal_phone_number: member?.personal_phone_number || '',
});

export const generateAdditionalInfoInitialValue = (
  member: TMember
): IAdditionalInfo => ({
  date_of_birth: member.date_of_birth || '',
  gender: member.gender || '',
  marital_status: member.marital_status,
  nationality: member.nationality,
});

export const additionalInfoSchema = object({
  date_of_birth: string().required('Date of birth is required'),
  gender: string(),
  marital_status: string(),
  nationality: string().required('Nationality is required'),
});
